import { cloneDeep, isEmpty, isEqual, isNil, keyBy, mapValues, merge, sortBy, sumBy, uniq } from 'lodash';
import compact from 'lodash/compact';
import flatMap from 'lodash/flatMap';
import type {
  ColumnErrors,
  ColumnMapping,
  CurrencyEnum,
  FileMapping,
  FileUploadMetadata,
  Fund,
  Portfolio,
  PortfolioNodeDesignationEnum,
  PortfolioParseResult,
  SubjectRangeAnalysis,
} from 'venn-api';
import { assertNotNil, isCashNode } from '../../../../../../venn-utils/src';
import { APPEND_ONLY, DataUploaderMode, DO_NOT_MAP, DO_NOT_MAP_ID, FUND, type RowData } from '../../types';
import type {
  HistoricalFundRange,
  HistoricalFundRangeAnalysis,
} from '../components/specialized/MultiHistoricalPortfolioContext';
import { isColumnDeleted } from '../mapping/helpers';
import type { DefaultPortfolioParseResultCategory } from '../components/common/MultiPortfolioReviewContext';
import { iterateFunds, toPathKey } from '../../utils/portfolioTree';
import { getTypeId } from '../../fetchers';
import type { CellRenderer, CellRendererProps } from '../../components/renderers/types';
import type { ExistingOwnerships, OwnershipSelectorState } from '../../InvestmentDataUploader';
import type { DropMenuCheckboxItem, DropMenuItem } from 'venn-ui-kit';

export const CASH_BORROWING = 'Cash Borrowing';
export const CASH_HOLDING = 'Cash Holding';

export const Mixed = Symbol('mixed');

export interface ErrorViewModel {
  seriesId: string;
  // note that if it's a missing value, rowIndex is negative
  // and could change as the number of missing values changes
  rowIndex: number;
  date: string;
  value: string;
  isValid: boolean;
  errors: string[];
}

interface AdditionalMappingMetadataPerSeries {
  isDeleted: boolean;
  isDisabled: boolean;
  isNew: boolean;
  isReturns: boolean;
}

export interface AdditionalMappingMetadata {
  appendOnlyId: number;
  deletedTypeId: number;
  fundTypeId: number;
  perSeries: Record<string, AdditionalMappingMetadataPerSeries>;
}

export interface ReviewCellRendererProps {
  currentMapping: FileMapping;
  errors: ErrorViewModel[];
  metadata: FileUploadMetadata;
  mode: DataUploaderMode;
  onMappingChange: (newMapping: FileMapping) => void;
  onBulkOwnershipChange: (selectedMappings: ColumnMapping[], updatedOwnerships: DropMenuCheckboxItem[]) => FileMapping;
  updateMappingOptimistically: (newMapping: FileMapping) => void;
  additionalMetadata: AdditionalMappingMetadata;
  ownershipSelectorState: OwnershipSelectorState;
  setOwnershipSelectorState: React.Dispatch<React.SetStateAction<OwnershipSelectorState>>;
  existingOwnerships: ExistingOwnerships;
  setExistingOwnerships: React.Dispatch<React.SetStateAction<ExistingOwnerships>>;
  availableOwnerships: DropMenuItem[];
  editingSeriesId: string | undefined;
  setEditingSeriesId: (seriesId: string) => void;
}

export interface ErrorsListRendererProps {
  errors: ErrorViewModel[];
  columns: ColumnMapping[];
  editingSeriesId: string;
  metadata: FileUploadMetadata;
  submitCorrections: (corrections: ErrorViewModel[]) => void;
  updateErrors: (error: ErrorViewModel, value: string) => void;
  isPrice: boolean;
}

export interface OwnershipItem {
  value: string;
  label: string;
  checked: boolean;
  disabled: boolean;
}

const areErrorsEqual = (err1: ErrorViewModel, err2: ErrorViewModel): boolean => {
  return (
    err1.seriesId === err2.seriesId &&
    // only use rowIndex to check equality if it's a real index
    (err1.rowIndex < 0 ? err1.date === err2.date : err1.rowIndex === err2.rowIndex)
  );
};

export function validateCells(newErrors: ErrorViewModel[], stateErrors: ErrorViewModel[]): ErrorViewModel[] {
  // update validity of stateErrors using the new errors
  const prevErrors = stateErrors.map((stateError) => {
    const updatedError = newErrors.find((newError) => areErrorsEqual(newError, stateError));
    if (updatedError) {
      // the error type could have changed so update it
      return {
        ...stateError,
        errors: updatedError.errors,
        isValid: false,
      };
    }
    // If we can't find a stateError in the newErrors array, that means it was corrected
    return {
      ...stateError,
      isValid: true,
    };
  });

  // there might be new errors that weren't there before so add them to the previous list of errors
  const actualNewErrors = newErrors.filter(
    (error) => !stateErrors.find((stateError) => areErrorsEqual(stateError, error)),
  );
  // maintain the order of the previous list of errors by simply appending the new errors
  return prevErrors.concat(actualNewErrors);
}

export function convertColumnErrorsToViewModels(columns: ColumnErrors[]): ErrorViewModel[] {
  return columns.reduce<ErrorViewModel[]>(
    (m, column) =>
      m.concat(
        column.errors.reduce<ErrorViewModel[]>((memo, e) => {
          const cellErrors = compact(
            e.cells.map((c) =>
              c.errors
                ? {
                    seriesId: c.seriesId,
                    rowIndex: c.index,
                    date: c.date,
                    value: c.value,
                    errors: c.errors,
                    isValid: false,
                  }
                : null,
            ),
          );
          return memo.concat(cellErrors);
        }, []),
      ),
    [],
  );
}

export function updateErrorValue(errors: ErrorViewModel[], error: ErrorViewModel, value: string): ErrorViewModel[] {
  const errorIndex = errors.indexOf(error);
  return [
    ...errors.slice(0, errorIndex),
    {
      ...error,
      value,
    },
    ...errors.slice(errorIndex + 1),
  ];
}

export function countData(columns: ColumnMapping[], mode: DataUploaderMode, isNew: boolean) {
  let filteredData;
  if (mode === DataUploaderMode.Returns) {
    filteredData = isNew ? columns.filter((c) => !c.fundId) : columns.filter((c) => !!c.fundId);
  } else {
    filteredData = isNew
      ? columns.filter((c) => !!c.fundId && c.typeId !== DO_NOT_MAP_ID)
      : columns.filter((c) => !c.fundId || c.typeId === DO_NOT_MAP_ID);
  }
  return filteredData.length;
}

/**
 * Return whether there are uncorrected errors for the given seriesId in the given list of errors.
 * @param errors if not provided, returns false
 * @param seriesId if not provided, all errors will be considered
 */
export function hasUncorrectedErrors(errors?: ErrorViewModel[], seriesId?: string): boolean {
  if (!errors) return false;
  if (!seriesId) return errors.some((e) => !e.isValid);
  return errors.some((e) => !e.isValid && e.seriesId === seriesId);
}

export const getColumnsToUpload = (columns: ColumnMapping[], metadata: FileUploadMetadata, errors: ErrorViewModel[]) =>
  columns.filter(
    (c) => c.newDataCount !== 0 && !isColumnDeleted(c, metadata) && !hasUncorrectedErrors(errors, c.seriesId),
  );

export type SerializedPath = {
  tag: 'serialized';
  value: string;
};

// Safe to use because path is a number array
const PATH_SEPARATOR = ',';

export const serializePath = (path: number[]): SerializedPath => {
  return {
    tag: 'serialized',
    value: path.join(PATH_SEPARATOR),
  };
};

export const deserializePath = (value: string): number[] => {
  return value.split(PATH_SEPARATOR).map((index) => parseInt(index, 10));
};

export const isZeroAllocationFund = (node: Portfolio): boolean => {
  assertNotNil(node.fund, 'should only be called for fund nodes');
  if (node.historical) {
    const allocations = assertNotNil(
      node.closingAllocationsTs,
      'historical fund node has no closing allocations timeseries',
    );
    return allocations.every(([_, value]) => value === 0.0);
  }
  return !node.allocation && !node.netAssets && !node.cashBalance;
};

/** Detect whether there are any funds inside this portfolio that need to be mapped */
export const hasUnmatchedFunds = (portfolio: Portfolio, excludedInvestments: Set<string>): boolean => {
  const hasUnmatchedFundsImpl = (portfolio: Portfolio, path: number[]): boolean => {
    if (isEmpty(portfolio.children)) {
      if (portfolio.fund) {
        // fund node
        const isExcluded = excludedInvestments.has(serializePath(path).value);
        const hasZeroAllocation = isZeroAllocationFund(portfolio);
        return isNil(portfolio.fund.id) && !isExcluded && !hasZeroAllocation && !isCashNode(portfolio);
      }
      return false; // a strategy without any children
    }
    for (const [index, child] of portfolio.children.entries()) {
      if (hasUnmatchedFundsImpl(child, [...path, index])) {
        return true;
      }
    }
    return false;
  };

  // The paths are prefixed with zero for root node
  return hasUnmatchedFundsImpl(portfolio, [0]);
};

/** Detect whether this portfolio node is a fund which needs to be mapped */
export const nodeNeedsMapping = ({ path, node: row }: RowData<Portfolio>, excludedInvestments: Set<string>) => {
  // if row is excluded no mapping is needed since it's gonna be ignored
  if (excludedInvestments.has(serializePath(path).value)) {
    return {
      isFund: !!row?.fund,
      needsMapping: false,
    };
  }
  if (row?.designation === 'CASH') {
    return {
      isFund: !!row?.fund,
      needsMapping: false,
    };
  }

  if (row?.fund) {
    // is this a fund?
    if (row?.fund.id) {
      // if it has an id, it's already mapped
      return {
        isFund: true,
        needsMapping: false,
      };
    } // if it doesn't have an id, it needs mapping
    return {
      isFund: true,
      needsMapping: true,
    };
  } // strategy nodes don't need mapping
  return {
    isFund: false,
    needsMapping: false,
  };
};

/** Detect whether this historical portfolio node has any duplicate funds */
export const historicalNodeIsDuplicated = (
  { root, node: row, path }: RowData<Portfolio>,
  date: number | undefined,
  excludedInvestments: Set<string>,
) => {
  if (!root || !path || !row || !date || !row.fund?.id) {
    return {
      isFund: false,
      duplicateInvestment: false,
    };
  }

  // level 0 is the root, so we start at level 1
  let strategy = root;
  for (let level = 1; level < path.length - 1; level++) {
    strategy = strategy.children[path[level]!]!;
  }

  return {
    isFund: true,
    duplicateInvestment: hasDuplicatedHistoricalInvestments(strategy, row, path, date, excludedInvestments),
  };
};

export const historicalPortfolioHasDuplicatedInvestments = (
  portfolio: Portfolio,
  excludedInvestments: Set<string>,
): boolean => {
  const historicalPortfolioHasDuplicatedInvestmentsImpl = (portfolio: Portfolio, path: number[]): boolean => {
    return portfolio.children?.some((child, index) => {
      const childPath = [...path, index];
      if (child.fund) {
        return child.closingAllocationsTs?.some((dt_alloc) => {
          return hasDuplicatedHistoricalInvestments(portfolio, child, childPath, dt_alloc[0]!, excludedInvestments);
        });
      }
      return historicalPortfolioHasDuplicatedInvestmentsImpl(child, childPath);
    });
  };

  return historicalPortfolioHasDuplicatedInvestmentsImpl(portfolio, [0]);
};

export const hasDuplicatedHistoricalInvestments = (
  parentStrategy: Portfolio,
  fundNode: Portfolio,
  fundPath: number[],
  date: number,
  excludedInvestments: Set<string>,
): boolean => {
  // If node is excluded or has zero or no allocation on a given date return false
  const allocationAtDate = fundNode.closingAllocationsTs?.find((x) => x[0] === date)?.[1];

  if (excludedInvestments.has(serializePath(fundPath).value) || !allocationAtDate) {
    return false;
  }

  // Check if parent strategy contains another child with the same fund and closing allocation at the same date
  return (
    parentStrategy.children.filter((anotherChild, index) => {
      const anotherChildPath = [...fundPath.slice(0, -1), index];

      if (excludedInvestments.has(serializePath(anotherChildPath).value)) {
        return false;
      }

      return (
        anotherChild.fund?.id === fundNode.fund?.id &&
        anotherChild.closingAllocationsTs?.some((x) => x[0] === date && !!x[1])
      );
    }).length > 1
  );
};

/**
 * Recursively updates the fund of a portfolio node at a specified path.
 *
 * @param node - The root portfolio node.
 * @param path - An array of indices representing the path to the target node.
 * @param fund - The new fund to be assigned to the target node.
 * @returns The updated portfolio with the fund remapped at the specified path.
 *
 * @example
 * const portfolio = {
 *   name: 'Root Portfolio',
 *   fund: null,
 *   children: [
 *     {
 *       name: 'Child Portfolio',
 *       fund: null,
 *       children: [
 *         { name: 'Grandchild Portfolio', fund: { id: '123' }, children: [] },
 *         { name: 'Old Fund', fund: { id: 'old-fund-id' }, children: [] }
 *       ]
 *     }
 *   ]
 * };
 * const path = [0, 1];
 * const newFund = { id: 'new-fund-id', name: 'New Fund' };
 * const updatedPortfolio = remapInvestment(portfolio, path, newFund);
 * `updatedPortfolio.children[0].children[1]` will have the new fund
 */
export const remapInvestment = (node: Portfolio, path: number[], fund: Fund): Portfolio => {
  if (path.length < 1) {
    return {
      ...node,
      name: fund.name,
      fund,
    };
  }
  const [head, ...tail] = path;
  return {
    ...node,
    children: (node.children ?? []).map((child, index) => {
      if (index === head) {
        return remapInvestment(child, tail, fund);
      }
      return child;
    }),
  };
};

export const changePortfolioNodeDesignation = (
  node: Portfolio,
  path: number[],
  designation: PortfolioNodeDesignationEnum,
): Portfolio => {
  if (path.length < 1) {
    if (!node.fund) {
      return node;
    }

    return {
      ...node,
      designation,
      name: designation === 'CASH' ? ((node.netAssets ?? 0) < 0 ? CASH_BORROWING : CASH_HOLDING) : node.fund.name,
      cashBalance: designation === 'CASH' ? (node.netAssets ?? 0) : 0,
      allocation: designation !== 'CASH' ? (node.netAssets ?? 0) : 0,
    };
  }
  const [head, ...tail] = path;
  return {
    ...node,
    children: (node.children ?? []).map((child, index) => {
      if (index === head) {
        return changePortfolioNodeDesignation(child, tail, designation);
      }
      return child;
    }),
  };
};

/** Find all funds ids within the portfolio */
export const getAllFundIds = (portfolio: Portfolio): string[] => {
  if (portfolio.fund) {
    return isNil(portfolio.fund.id) ? [] : [portfolio.fund.id];
  }

  return flatMap((portfolio.children ?? []).map((child) => getAllFundIds(child)));
};

/**
 * Actually remove excluded and unmapped investments from portfolio.
 * This needs to be called right before we are about to send a request to backend to persist them
 */
export const removeExcludedAndUnmappedInvestments = (
  portfolio: Portfolio,
  excludedInvestments: Set<string>,
): Portfolio => {
  const excludedPaths = Array.from(excludedInvestments).map(deserializePath);

  /**
   * This "inefficient" helper function returns indices of children that should be excluded for a given path
   *
   * First, it finds out excluded paths that are direct children of a given path.
   * E.g. if current path is [0, 2, 1] then direct children can be [0, 2, 1, 1] and [0, 2, 1, 3]
   *
   * Then, it returns only last index from each direct children path (e.g. 1 and 3 in the example above)
   */
  const getDirectChildrenExcludedIndexes = (path: number[]): number[] => {
    return excludedPaths
      .filter(
        (excludedPath) => excludedPath.length === path.length + 1 && isEqual(excludedPath.slice(0, path.length), path),
      )
      .map((excludedPath) => excludedPath[excludedPath.length - 1]!);
  };

  /**
   * This dfs should run bottom-up, we first process all descendants and then the node itself.
   * If we do it top-down it will mess up indexing
   */
  const removeExcludedInvestmentsImpl = (portfolioNode: Portfolio, path: number[]) => {
    if (!isNil(portfolioNode.fund)) {
      return;
    }

    portfolioNode.children?.forEach((child, index) => {
      removeExcludedInvestmentsImpl(child, [...path, index]);
    });

    const excludedIndices = getDirectChildrenExcludedIndexes(path);

    const isMapped = (child: Portfolio) => !child.fund || child.fund.id;

    // Exclude children by indices if they were explicitly excluded by user
    // And also exclude children that are funds without ids (unmapped)
    portfolioNode.children = portfolioNode.children?.filter(
      (child, index) => !excludedIndices.includes(index) && (isMapped(child) || isCashNode(child)),
    );
  };

  const clonedPortfolio = cloneDeep(portfolio);

  removeExcludedInvestmentsImpl(clonedPortfolio, [0]);

  return clonedPortfolio;
};

/**
 * Recalculates allocations, cash balances and net assets for strategies by ignoring excluded investments.
 *
 * Note: This doesn't change allocations, cash balances or net assets for excluded investments themselves, only for strategies that contain them.
 */
export const recalculateAssets = (portfolio: Portfolio, excludedInvestments: Set<string>): Portfolio => {
  const recalculateAssetsImpl = (portfolioNode: Portfolio, path: number[]) => {
    if (!isNil(portfolioNode.fund)) {
      return;
    }

    portfolioNode.children?.forEach((child, index) => {
      recalculateAssetsImpl(child, [...path, index]);
    });

    const filteredChildren = portfolioNode.children?.filter((_, index) => {
      return !excludedInvestments.has(serializePath([...path, index]).value);
    });
    portfolioNode.allocation = sumBy(filteredChildren, 'allocation');
    portfolioNode.cashBalance = sumBy(filteredChildren, 'cashBalance');
    portfolioNode.netAssets = sumBy(filteredChildren, 'netAssets');
  };

  const clonedPortfolio = cloneDeep(portfolio);

  recalculateAssetsImpl(clonedPortfolio, [0]);

  return clonedPortfolio;
};

export const getAllRanges = (range: SubjectRangeAnalysis): HistoricalFundRangeAnalysis =>
  merge(
    range.investmentId
      ? {
          [range.investmentId]: {
            start: range.start,
            end: range.end,
          },
        }
      : {},
    ...(range.rangeAnalyses ? range.rangeAnalyses.map(getAllRanges) : []),
  );

export const historicalNodeHasShortHistory = (allAllocs: number[][] | undefined, range?: HistoricalFundRange) => {
  if (!range || !allAllocs) {
    return false;
  }

  const allocTimestamps = allAllocs.filter((x) => !!x[1]).map((x) => x[0]!);

  if (isEmpty(allocTimestamps)) {
    return false;
  }

  const earliestAllocation = Math.min(...allocTimestamps);
  const latestAllocation = Math.max(...allocTimestamps);

  return (
    earliestAllocation < range.start ||
    latestAllocation < range.start ||
    earliestAllocation > range.end ||
    latestAllocation > range.end
  );
};

export const getFundsWithShortHistory = (
  portfolio: Portfolio,
  rangeAnalysis?: HistoricalFundRangeAnalysis,
): string[] => {
  if (!rangeAnalysis) {
    return [];
  }

  const fundsWithShortHistory: string[] = [];

  const getFundsWithShortHistoryImpl = (portfolioNode: Portfolio) => {
    const fundId = portfolioNode.fund?.id;
    if (fundId) {
      if (historicalNodeHasShortHistory(portfolioNode.closingAllocationsTs, rangeAnalysis[fundId])) {
        fundsWithShortHistory.push(assertNotNil(portfolioNode.fund?.name));
      }
    } else {
      portfolioNode.children?.forEach((child) => getFundsWithShortHistoryImpl(child));
    }
  };

  getFundsWithShortHistoryImpl(portfolio);

  return uniq(fundsWithShortHistory);
};

export const getPortfolioAllocationRange = (
  portfolio: Portfolio,
):
  | {
      from: number;
      to: number;
    }
  | undefined => {
  if (!portfolio.historical) {
    return undefined;
  }

  const allAllocationDates: number[] = [];

  const getAllPortfolioAllocationDates = (portfolioNode: Portfolio) => {
    portfolioNode.closingAllocationsTs?.forEach(([date, _]) => allAllocationDates.push(date!));
    portfolioNode.children?.forEach((child) => getAllPortfolioAllocationDates(child));
  };

  getAllPortfolioAllocationDates(portfolio);

  if (isEmpty(allAllocationDates)) {
    return undefined;
  }

  const sortedDates = sortBy(allAllocationDates);

  return {
    from: sortedDates[0]!,
    to: sortedDates[sortedDates.length - 1]!,
  };
};

export const getDefaultPortfolioParseResultCategory = (
  result: PortfolioParseResult,
): DefaultPortfolioParseResultCategory => {
  if (isNil(result.parsedPortfolio.id)) {
    return 'new' as const;
  }
  return 'existing' as const;
};

/** Check if any investment in the portfolio lacks strategy assignment */
export const hasUnassignedInvestments = (
  portfolio: Portfolio,
  excludedInvestments: Set<string>,
  opts: {
    hasManualStrategyAssignment: boolean;
  },
): boolean => {
  if (!opts.hasManualStrategyAssignment) {
    return false;
  }

  for (const { node, path } of iterateFunds(portfolio, [0])) {
    const key = toPathKey(path);
    const isExcluded = excludedInvestments.has(key);
    const hasZeroAllocation = isZeroAllocationFund(node);
    const isCash = isCashNode(node);

    if (isExcluded || hasZeroAllocation || isCash) continue;

    const fundId = node.fund?.id;
    if (!fundId) continue; // only consider mapped investments

    // Assigned if fund is not directly under root (i.e., there is at least one strategy level)
    const isAssigned = path.length > 2;

    if (!isAssigned) {
      return true;
    }
  }

  return false;
};

export const isPortfolioNeedAssigning = (
  portfolio?: Portfolio,
  path?: number[],
  excludedInvestments: Set<string> | undefined = new Set(),
  strategyData?: {
    hasManualStrategyAssignment: boolean;
  },
): boolean | string | undefined => {
  const fundId = portfolio?.fund?.id;
  const isExcluded = path ? excludedInvestments?.has(serializePath(path)?.value) : false;
  return (
    strategyData?.hasManualStrategyAssignment &&
    fundId &&
    !isExcluded &&
    !nodeNeedsMapping({ path, node: portfolio } as RowData<Portfolio>, excludedInvestments).needsMapping
  );
};

export const additionalMetadata = (
  currentMapping: FileMapping,
  metadata: FileUploadMetadata,
  mode: DataUploaderMode,
  errors: ErrorViewModel[],
): AdditionalMappingMetadata => {
  const additionalMetadataPerSeries: Record<string, AdditionalMappingMetadataPerSeries> = {};
  const appendOnlyId = getTypeId(metadata, 'appendType', APPEND_ONLY);
  const fundTypeId = getTypeId(metadata, 'dataType', FUND);
  const deletedTypeId = getTypeId(metadata, 'dataType', DO_NOT_MAP);

  currentMapping.columns.forEach((column) => {
    const isDeleted = Number(column.typeId) === deletedTypeId;
    const isReturns = mode === DataUploaderMode.Returns;
    const isNew = isDataNew(isReturns, !!column.fundId, !!column.portfolioNodeId);
    const isDisabled = isCellDisabled(column, metadata, errors, column.seriesId);

    additionalMetadataPerSeries[column.seriesId] = {
      isDeleted,
      isDisabled,
      isNew,
      isReturns,
    };
  });

  return {
    appendOnlyId,
    deletedTypeId,
    fundTypeId,
    perSeries: additionalMetadataPerSeries,
  };
};

export const isDataNew = (isReturns: boolean, hasFundId: boolean, hasPortfolioNodeId: boolean): boolean => {
  return !hasFundId && (isReturns || !hasPortfolioNodeId);
};

export const isCellDisabled = (
  columnMapping: ColumnMapping,
  metadata: FileUploadMetadata,
  errors: ErrorViewModel[],
  seriesId: string,
) => {
  const { newDataCount } = columnMapping;
  const isDeleted = isColumnDeleted(columnMapping, metadata);
  return isDeleted || newDataCount === 0 || hasUncorrectedErrors(errors, seriesId);
};

export const getFirstCustomErrorMessage = (
  errors: ErrorViewModel[],
  seriesId: string,
  useCustomErrorMessage?: boolean,
) =>
  !useCustomErrorMessage ? undefined : errors.filter((e) => e.seriesId === seriesId).map((e) => e.errors?.[0])?.[0];

export const createCellRenderer =
  ({ renderer, ...rendererProps }: CellRendererProps & { renderer: CellRenderer }) =>
  (columnMapping: ColumnMapping, index: number, count: number) =>
    renderer({ columnMapping, index, count, ...rendererProps });

// Format options
export enum FormatType {
  ReturnPercentage = 'ReturnPercentage',
  ReturnDecimal = 'ReturnDecimal',
  Price = 'Price',
}
const returnPercentageOption = {
  value: FormatType.ReturnPercentage,
  label: 'Return: Percentage',
};
const returnDecimalOption = {
  value: FormatType.ReturnDecimal,
  label: 'Return: Decimal',
};
const priceOption = {
  value: FormatType.Price,
  label: 'Price',
};
export const formatOptions: [DropMenuItem<FormatType>, DropMenuItem<FormatType>, DropMenuItem<FormatType>] = [
  returnPercentageOption,
  returnDecimalOption,
  priceOption,
];

export enum MetricType {
  Return = 1,
  Price = 2,
}

export const determineFormat = (row: ColumnMapping) =>
  row.metricId === MetricType.Return
    ? row.isPercent
      ? FormatType.ReturnPercentage
      : FormatType.ReturnDecimal
    : FormatType.Price;

export const parseFormat = (format: FormatType): { metricId: MetricType; isPercent: boolean | typeof Mixed } => {
  if (format === FormatType.Price) {
    return {
      metricId: MetricType.Price,
      isPercent: Mixed,
    };
  }
  return {
    metricId: MetricType.Return,
    isPercent: format === FormatType.ReturnPercentage,
  };
};

type OptionalMixed<T> = T | typeof Mixed;

export interface ColumnMappingMetadata {
  appendId?: OptionalMixed<number>;
  currency?: OptionalMixed<CurrencyEnum>;
  isPercent?: OptionalMixed<boolean>;
  metricId?: OptionalMixed<number>;
  ownerContextIds?: string[];
  typeId?: number;
  fund?: Fund | string;
}

interface UpdateColumnStateForNewReviewProps {
  mapping: FileMapping;
  columns: ColumnMapping[];
  values: ColumnMappingMetadata;
  seriesId?: string;
}

type MappingUpdaterForNewReview = (
  columns: ColumnMapping[],
  changes: ColumnMappingMetadata,
  seriesId?: string,
  mode?: DataUploaderMode,
) => ColumnMapping[];

export const updateMappingColumnStateForNewReview = (
  { mapping, columns, values, seriesId }: UpdateColumnStateForNewReviewProps,
  updater: MappingUpdaterForNewReview,
  mode?: DataUploaderMode,
): FileMapping => ({
  ...mapping,
  columns: [...mapping.columns.filter((c) => !columns.includes(c)), ...updater(columns, values, seriesId, mode)],
});

export const updateColumnMappingsForNewReviewByGlobalChange: MappingUpdaterForNewReview = (
  columns: ColumnMapping[],
  values: ColumnMappingMetadata,
): ColumnMapping[] => {
  const { metricId, isPercent, currency, appendId } = values;
  const newColumns = columns.map((c) => ({
    ...c,
    isPercent: isPercent !== undefined && isPercent !== Mixed ? isPercent : c.isPercent,
    metricId: metricId && metricId !== Mixed ? metricId : c.metricId,
    currency: currency && currency !== Mixed ? currency : c.currency,
    appendId: appendId && appendId !== Mixed ? appendId : c.appendId,
  }));
  return newColumns;
};

export const determineOwnershipState = (
  availableOwnerships: DropMenuItem[] | DropMenuCheckboxItem[],
  mappings: ColumnMapping[],
  existingOwnerships: ExistingOwnerships,
): DropMenuCheckboxItem[] => {
  const totalMappings = mappings.length;
  const seriesIsNewFund = mapValues(keyBy(mappings, 'seriesId'), (m) => !m.fundId);
  const seriesWithOwnership: string[] = [];
  // Frequency map of Ownership IDs
  const counts = new Map<string, number>();

  for (const row of mappings) {
    if (row.ownerContextIds?.length) {
      seriesWithOwnership.push(row.seriesId);
      for (const id of row.ownerContextIds) {
        counts.set(id, (counts.get(id) || 0) + 1);
      }
    }
  }

  const allSelectedRowsHaveOwnership = seriesWithOwnership.length === totalMappings;

  // Determine the status for each ownership
  return availableOwnerships.map((ownership) => {
    const existingFundsHaveOwnership =
      allSelectedRowsHaveOwnership &&
      seriesWithOwnership.some(
        (seriesId) => existingOwnerships[seriesId]?.includes(ownership.value) && !seriesIsNewFund[seriesId],
      );
    const allSelectedRowsOriginallyHaveCurrentOwnership = mappings.every((m) =>
      existingOwnerships[m.seriesId]?.includes(ownership.value),
    );
    const count = counts.get(ownership.value) || 0;
    const isChecked = (count > 0 || totalMappings > 0) && count === totalMappings;
    const isIndeterminate = !isChecked && count > 0;
    return {
      label: ownership.label,
      value: ownership.value,
      checked: isChecked,
      indeterminate: isIndeterminate,
      disabled: !isIndeterminate && existingFundsHaveOwnership && allSelectedRowsOriginallyHaveCurrentOwnership,
    };
  });
};
